import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Bookmark, Check, ChevronDown, Group, ListFilter, Plus, Search, Sparkles, X } from "lucide-react";
import type { Domain, FieldDef, GroupByOption, SearchField, SearchFilter } from "../core";
import {
    DATE_GRANULARITIES,
    DEFAULT_DATE_GRANULARITY,
    GRANULARITY_LABELS,
    buildGroupByEntry,
    computeChipSegments,
    groupByEntryField,
    parseGroupByEntry,
    toggleGranularityInSegment as toggleGranularityInSegmentPure,
} from "../core";
import type { ChipSegment, DateGranularity } from "../core";

// Ad-hoc chip = a user-typed search committed as a custom-filter domain.
interface SearchPanelProps {
    searchfields: SearchField[];
    fieldDefs: FieldDef[];
    filters: SearchFilter[];
    activeFilterKeys: Set<string>;
    onToggleFilter: (key: string) => void;
    groupbys: GroupByOption[];
    // Ordered list of currently-active group-by fields (outermost first).
    groupBy: string[];
    onToggleGroupBy: (field: string) => void;
    onRemoveGroupBy: (field: string) => void;
    // Replace the whole group-by list (used by the Custom Group dialog).
    onSetGroupBy: (fields: string[]) => void;
    // Custom fields the user has touched this session — rendered as extra
    // Items in the Group By section so removing the chip still leaves a
    // way to re-toggle them without re-opening the dialog.
    sessionCustomGroupFields: string[];
    onAddSessionCustomGroupFields: (fields: string[]) => void;
    // Ad-hoc filters created from free-text search this session. Stay listed
    // in the Filters card so the user can re-toggle them after × on the chip.
    sessionCustomFilters: SearchFilter[];
    onCreateAdHocFilter: (spec: { fieldLabel: string; text: string; domain: Domain }) => void;
    savedFilters: SearchFilter[];
    onDeleteSaved: (key: string) => void;
    onSaveCurrent: (name: string) => void;
    canSave: boolean;
}

// Field types that make sense as a group-by axis. Continuous/aggregate
// (decimal/float), collection (one2many/many2many) and unique (uuid) types
// are excluded — grouping by those is either meaningless or unbounded.
const GROUPABLE_TYPES = new Set(["enum", "selection", "reference", "boolean", "date", "datetime", "integer", "char"]);
const isGroupable = (def: FieldDef): boolean => GROUPABLE_TYPES.has(def.type);

const humanizeField = (name: string): string =>
    name
        .replace(/_id$/, "")
        .replace(/[_.]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();

const escapeForJsonString = (s: string): string => s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

function substituteDomain(template: string, text: string): Domain | null {
    try {
        const json = template.replace(/\{self\}/g, escapeForJsonString(text));
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? (parsed as Domain) : null;
    } catch {
        return null;
    }
}

// Pilled-search: typing offers per-searchfield suggestions ("Name: foo", …);
// picking one (or pressing Enter for the default field) commits a chip with the
// substituted domain. No free-text `search` param — everything is a domain pill.
// Popover opens on focus and shows Suggestions (when typing) + Filters + Group
// By + Favourites; outside-click closes.
export function SearchPanel(props: SearchPanelProps) {
    const {
        searchfields,
        fieldDefs,
        filters,
        activeFilterKeys,
        onToggleFilter,
        groupbys,
        groupBy,
        onToggleGroupBy,
        onRemoveGroupBy,
        onSetGroupBy,
        sessionCustomGroupFields,
        onAddSessionCustomGroupFields,
        sessionCustomFilters,
        onCreateAdHocFilter,
        savedFilters,
        onDeleteSaved,
        onSaveCurrent,
        canSave,
    } = props;

    // Active group-by set keyed by bare field name (strips any :granularity
    // suffix) so the "is this field active?" check still works for date /
    // datetime fields whose stored entry is `order_date:month`.
    const groupByActive = useMemo(() => new Set(groupBy.map(groupByEntryField)), [groupBy]);

    // Field-type lookup memo + bucket helpers — used both for the "default to
    // :month when toggling on a date field" wrap below and for the per-chip
    // bucket dropdown that lets the user re-pick the granularity.
    const fieldTypeByName = useMemo(() => {
        const m = new Map<string, string>();
        for (const def of fieldDefs) m.set(def.name, def.type);
        return m;
    }, [fieldDefs]);
    const isDateField = (name: string): boolean => {
        const t = fieldTypeByName.get(name);
        return t === "date" || t === "datetime";
    };
    // For any incoming entry, encode the default `:month` bucket when the
    // underlying field is a date/datetime and the entry has no explicit
    // granularity yet. Otherwise return as-is — non-date fields stay bare,
    // and explicit granularities (set by the user via the chip dropdown)
    // round-trip untouched.
    const encodeWithDefaultBucket = (entry: string): string => {
        const parsed = parseGroupByEntry(entry);
        if (parsed.granularity) return entry;
        return isDateField(parsed.field)
            ? buildGroupByEntry({ field: parsed.field, granularity: DEFAULT_DATE_GRANULARITY })
            : parsed.field;
    };
    const toggleWithBucket = (field: string) => onToggleGroupBy(encodeWithDefaultBucket(field));
    const setWithBucket = (entries: string[]) => onSetGroupBy(entries.map(encodeWithDefaultBucket));

    // Group consecutive same-field entries into ONE chip segment so the date
    // field reads as one chip with its full bucket chain (Year › Quarter ›
    // Month) instead of repeating the label per granularity. Pure derivation
    // — see core/groupby/dateBucket.ts.
    const chipSegments = useMemo<ChipSegment[]>(() => computeChipSegments(groupBy), [groupBy]);

    // Toggle one granularity within a chip segment — pure transform of the
    // groupBy array (insert in hierarchical order on add, drop on remove).
    const toggleGranularityInSegment = (segment: ChipSegment, granularity: DateGranularity) => {
        onSetGroupBy(toggleGranularityInSegmentPure(groupBy, segment, granularity));
    };

    // Session-custom group items resolved against fieldDefs (label fallback to
    // humanized field name). Each session-custom item carries a single-axis
    // `fields:[name]` so its click can still toggle one level on/off, while
    // the DSL's predefined multi-axis presets click-to-replace the whole stack.
    const predefinedSingleFieldKeys = useMemo(
        () => new Set(groupbys.filter((g) => g.fields.length === 1).map((g) => g.fields[0])),
        [groupbys],
    );
    const sessionGroupItems = useMemo<GroupByOption[]>(() => {
        return sessionCustomGroupFields
            .filter((name) => !predefinedSingleFieldKeys.has(name))
            .map((name) => {
                const def = fieldDefs.find((d) => d.name === name);
                return { fields: [name], label: def?.label ?? humanizeField(name) };
            });
    }, [sessionCustomGroupFields, fieldDefs, predefinedSingleFieldKeys]);

    // Strict-order comparison so multi-axis presets light up only when their
    // exact ordered tuple is the active group-by stack.
    const arraysEqual = (a: string[], b: string[]): boolean =>
        a.length === b.length && a.every((v, i) => v === b[i]);

    // Custom group-by candidates for the dialog: all groupable model fields
    // not already offered as a single-axis predefined preset. Session-promoted
    // fields stay listed so the user can uncheck them in the dialog too.
    const customGroupFields = useMemo(
        () => fieldDefs.filter(isGroupable).filter((f) => !predefinedSingleFieldKeys.has(f.name)),
        [fieldDefs, predefinedSingleFieldKeys],
    );

    // Resolve a label for any active group-by entry. Accepts the wire-form
    // entry (`order_date:month` or bare `order_date`) and looks up the bare
    // field's label via DSL → fieldDef → humanized fallback.
    const labelForField = (entry: string): string => {
        const field = groupByEntryField(entry);
        const fromDsl = groupbys.find((g) => g.fields.length === 1 && g.fields[0] === field)?.label;
        if (fromDsl) return fromDsl;
        const fromDef = fieldDefs.find((d) => d.name === field)?.label;
        return fromDef ?? humanizeField(field);
    };

    const [text, setText] = useState("");
    const [open, setOpen] = useState(false);
    const [activeSug, setActiveSug] = useState(0);
    // Floating Custom-Group dialog: opens *over* the popover (not inline), so
    // the popover height stays stable. The dialog stages its own selection and
    // only commits on Apply via onSetGroupBy(...).
    const [customDialogOpen, setCustomDialogOpen] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const openCustomDialog = () => setCustomDialogOpen(true);
    const closeCustomDialog = () => setCustomDialogOpen(false);
    const openSaveDialog = () => setSaveDialogOpen(true);
    const closeSaveDialog = () => setSaveDialogOpen(false);

    // Reset highlight to the first suggestion whenever the visible suggestion
    // set changes (text edited or searchfield list swapped).
    useEffect(() => {
        setActiveSug(0);
    }, [text, searchfields]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        // Escape closes the popover from anywhere — but defer to any open child
        // dialog (Custom Group / Save Favourite); they own Escape first.
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (customDialogOpen || saveDialogOpen) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, customDialogOpen, saveDialogOpen]);

    const activeFilterChips = [...filters, ...savedFilters, ...sessionCustomFilters].filter((f) => activeFilterKeys.has(f.key));
    const trimmed = text.trim();
    const showSuggestions = trimmed.length > 0 && searchfields.length > 0;

    const addChipFor = (field: SearchField) => {
        if (!trimmed) return;
        const domain = substituteDomain(field.filterDomain, trimmed);
        if (!domain) return;
        onCreateAdHocFilter({ fieldLabel: field.label, text: trimmed, domain });
        setText("");
    };

    // Backspace on an empty input pops the visually-rightmost chip (matches
    // Gmail / Slack pilled-input UX). Priority mirrors render order in reverse:
    // group-by → active filters (which now includes ad-hoc).
    const popLastChip = (): boolean => {
        if (groupBy.length > 0) {
            onRemoveGroupBy(groupBy[groupBy.length - 1]);
            return true;
        }
        if (activeFilterChips.length > 0) {
            onToggleFilter(activeFilterChips[activeFilterChips.length - 1].key);
            return true;
        }
        return false;
    };

    const onInputKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const target = searchfields[activeSug] ?? searchfields[0];
            if (target) addChipFor(target);
        } else if (e.key === "ArrowDown") {
            if (showSuggestions) {
                e.preventDefault();
                setActiveSug((i) => (i + 1) % searchfields.length);
            }
        } else if (e.key === "ArrowUp") {
            if (showSuggestions) {
                e.preventDefault();
                setActiveSug((i) => (i - 1 + searchfields.length) % searchfields.length);
            }
        } else if (e.key === "Escape") {
            if (text) setText("");
            else setOpen(false);
        } else if (e.key === "Backspace" && text === "") {
            if (popLastChip()) e.preventDefault();
        }
    };

    return (
        <div className="search-panel" ref={rootRef}>
            <div className={`search-panel__input ${open ? "is-open" : ""}`}>
                <Search size={14} className="search-panel__input-icon" />

                {activeFilterChips.map((f) => (
                    <span className="search-panel__chip search-panel__chip--filter" key={f.key} data-viewid="ede-search-chip" data-filter-key={f.key}>
                        <ListFilter size={13} className="search-panel__chip-icon" />
                        {f.fieldLabel && f.text != null ? (
                            <>
                                <span className="search-panel__chip-key">{f.fieldLabel}:</span>
                                {f.text}
                            </>
                        ) : (
                            f.label
                        )}
                        <button
                            className="search-panel__chip-close"
                            onClick={() => onToggleFilter(f.key)}
                            aria-label={`Remove ${f.label}`}
                        >
                            <X size={12} />
                        </button>
                    </span>
                ))}
                {groupBy.length > 0 ? (
                    <span
                        className="search-panel__chip search-panel__chip--group"
                        onClick={(e) => {
                            // Chip is its own action surface — open ONLY the dialog,
                            // do not also flip the popover open. stopPropagation
                            // prevents any parent input/focus from also triggering it.
                            e.stopPropagation();
                            openCustomDialog();
                        }}
                        role="button"
                        tabIndex={0}
                        title="Click to edit group by"
                    >
                        <Group size={13} className="search-panel__chip-icon" />
                        <span className="search-panel__chip-path">
                            {chipSegments.map((seg, segIdx) => {
                                const dateField = isDateField(seg.field);
                                const granularities = seg.entries
                                    .map((e) => e.granularity)
                                    .filter((g): g is DateGranularity => !!g);
                                const chainLabel = granularities.map((g) => GRANULARITY_LABELS[g]).join(" › ");
                                return (
                                    <span key={`${seg.field}-${seg.startIndex}`} className="search-panel__chip-path-segment">
                                        {segIdx > 0 ? <span className="search-panel__chip-path-sep">›</span> : null}
                                        {labelForField(seg.entries[0].entry)}
                                        {dateField && granularities.length > 0 ? (
                                            <DropdownMenu.Root>
                                                <DropdownMenu.Trigger asChild>
                                                    <button
                                                        type="button"
                                                        className="search-panel__chip-bucket"
                                                        onClick={(e) => e.stopPropagation()}
                                                        aria-label={`${labelForField(seg.entries[0].entry)} buckets: ${chainLabel}`}
                                                        title={`Group by ${chainLabel}`}
                                                    >
                                                        <span>{chainLabel}</span>
                                                        <ChevronDown size={10} />
                                                    </button>
                                                </DropdownMenu.Trigger>
                                                <DropdownMenu.Portal>
                                                    <DropdownMenu.Content
                                                        className="rdx-menu"
                                                        align="start"
                                                        sideOffset={4}
                                                        collisionPadding={12}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {DATE_GRANULARITIES.map((g) => {
                                                            const checked = granularities.includes(g);
                                                            // Disable unchecking the LAST remaining granularity
                                                            // — the user would have a chip with no bucket.
                                                            const isLast = checked && granularities.length === 1;
                                                            return (
                                                                <DropdownMenu.CheckboxItem
                                                                    key={g}
                                                                    className={`rdx-menu__item ${checked ? "is-active" : ""}`}
                                                                    checked={checked}
                                                                    disabled={isLast}
                                                                    onCheckedChange={() => toggleGranularityInSegment(seg, g)}
                                                                    onSelect={(e) => e.preventDefault()}
                                                                >
                                                                    <span className="rdx-menu__tick">
                                                                        {checked ? <Check size={12} /> : null}
                                                                    </span>
                                                                    <span className="rdx-menu__item-primary">{GRANULARITY_LABELS[g]}</span>
                                                                </DropdownMenu.CheckboxItem>
                                                            );
                                                        })}
                                                    </DropdownMenu.Content>
                                                </DropdownMenu.Portal>
                                            </DropdownMenu.Root>
                                        ) : null}
                                    </span>
                                );
                            })}
                        </span>
                        <button
                            className="search-panel__chip-close"
                            onClick={(e) => {
                                e.stopPropagation();
                                groupBy.forEach((f) => onRemoveGroupBy(f));
                            }}
                            aria-label="Clear group by"
                            title="Clear group by"
                        >
                            <X size={12} />
                        </button>
                    </span>
                ) : null}

                <input
                    className="search-panel__field"
                    value={text}
                    placeholder={searchfields.length ? "Search…" : "No search fields"}
                    onChange={(e) => setText(e.target.value)}
                    onFocus={() => setOpen(true)}
                    // After Escape, the input keeps focus so onFocus won't fire
                    // again — double-click re-opens the popover without losing
                    // the cursor.
                    onDoubleClick={() => setOpen(true)}
                    onKeyDown={onInputKey}
                    disabled={searchfields.length === 0}
                    data-viewid="ede-search-input"
                />
                <button
                    type="button"
                    className={`search-panel__toggle ${open ? "is-open" : ""}`}
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close search options" : "Open search options"}
                    aria-expanded={open}
                    data-viewid="ede-search-toggle"
                >
                    <ChevronDown size={14} />
                </button>
            </div>

            {open ? (
                <div className="search-panel__popover">
                    {showSuggestions ? (
                        /* While typing → suggestions ONLY. The three filter/
                           group-by/favourites cards step out so the user can
                           focus on picking which searchfield to commit. */
                        <div className="search-panel__list search-panel__list--sug">
                            <div className="search-panel__section">
                                <div className="search-panel__section-head">Search by</div>
                                {searchfields.map((f, i) => (
                                    <button
                                        type="button"
                                        key={f.name}
                                        className={`search-panel__item search-panel__item--sug ${i === activeSug ? "is-active" : ""}`}
                                        onMouseEnter={() => setActiveSug(i)}
                                        onClick={() => addChipFor(f)}
                                    >
                                        <span className="search-panel__item-label">
                                            <span className="search-panel__chip-key">{f.label}:</span> {trimmed}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                    <div className="search-panel__list">
                        {/* ── Filters card ─────────────────────────────────── */}
                        <div className="search-panel__card">
                            <div className="search-panel__card-head">
                                <ListFilter size={14} />
                                <span>Filters</span>
                            </div>
                            <div className="search-panel__card-body">
                                {(() => {
                                    // Section lists predefined filters + only the INACTIVE
                                    // session-custom ones. An active ad-hoc filter is already
                                    // visible as a chip in the search bar — duplicating it
                                    // here would add clutter. As soon as the user × removes
                                    // the chip, the filter reappears here as a toggleable row.
                                    const inactiveCustom = sessionCustomFilters.filter(
                                        (f) => !activeFilterKeys.has(f.key),
                                    );
                                    if (filters.length === 0 && inactiveCustom.length === 0) {
                                        return <EmptyState icon={<ListFilter size={22} />} message="No predefined options" />;
                                    }
                                    return (
                                        <>
                                            {filters.map((f) => (
                                                <Item
                                                    key={f.key}
                                                    active={activeFilterKeys.has(f.key)}
                                                    label={f.label}
                                                    onClick={() => onToggleFilter(f.key)}
                                                    viewProps={{ "data-viewid": "ede-search-filter", "data-filter-key": f.key }}
                                                />
                                            ))}
                                            {inactiveCustom.length > 0 && filters.length > 0 ? (
                                                <div className="search-panel__card-divider" />
                                            ) : null}
                                            {inactiveCustom.map((f) => (
                                                <Item
                                                    key={f.key}
                                                    active={false}
                                                    label={f.label}
                                                    onClick={() => onToggleFilter(f.key)}
                                                    viewProps={{ "data-viewid": "ede-search-filter", "data-filter-key": f.key }}
                                                />
                                            ))}
                                        </>
                                    );
                                })()}
                            </div>
                            <button type="button" className="search-panel__card-footer" disabled title="Coming soon">
                                <Plus size={12} />
                                <span>Custom Filter</span>
                            </button>
                        </div>

                        {/* ── Group By card ────────────────────────────────── */}
                        <div className="search-panel__card">
                            <div className="search-panel__card-head">
                                <Group size={14} />
                                <span>Group By</span>
                            </div>
                            <div className="search-panel__card-body">
                                {groupbys.length === 0 && sessionGroupItems.length === 0 ? (
                                    <EmptyState icon={<Group size={22} />} message="No predefined options" />
                                ) : (
                                    <>
                                        {groupbys.map((g) => {
                                            // Predefined preset = atomic. Click replaces the whole
                                            // group-by stack with this preset's fields; clicking
                                            // again clears the stack. Date/datetime fields in the
                                            // preset auto-encode `:month` so the chip dropdown has
                                            // a granularity to swap.
                                            const active = arraysEqual(groupBy.map(groupByEntryField), g.fields);
                                            return (
                                                <Item
                                                    key={g.fields.join(",")}
                                                    active={active}
                                                    label={g.label}
                                                    onClick={() => setWithBucket(active ? [] : g.fields)}
                                                    viewProps={{ "data-viewid": "ede-search-groupby", "data-groupby-key": g.fields.join(",") }}
                                                />
                                            );
                                        })}
                                        {sessionGroupItems.length > 0 && groupbys.length > 0 ? (
                                            <div className="search-panel__card-divider" />
                                        ) : null}
                                        {sessionGroupItems.map((g) => {
                                            // Session-custom items are always single-axis; keep the
                                            // toggle-append semantics so the user can nest them.
                                            const field = g.fields[0];
                                            return (
                                                <Item
                                                    key={field}
                                                    active={groupByActive.has(field)}
                                                    label={g.label}
                                                    onClick={() => toggleWithBucket(field)}
                                                    viewProps={{ "data-viewid": "ede-search-groupby", "data-groupby-key": field }}
                                                />
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                            {customGroupFields.length > 0 ? (
                                <button
                                    type="button"
                                    className={`search-panel__card-footer ${customDialogOpen ? "is-open" : ""}`}
                                    onClick={openCustomDialog}
                                >
                                    <Plus size={12} />
                                    <span>Custom Group</span>
                                    <ChevronDown size={12} className="search-panel__card-footer-chev" />
                                </button>
                            ) : null}
                        </div>

                        {/* ── Favourites card ──────────────────────────────── */}
                        <div className="search-panel__card">
                            <div className="search-panel__card-head">
                                <Bookmark size={14} />
                                <span>Favourites</span>
                            </div>
                            <div className="search-panel__card-body">
                                {savedFilters.length === 0 ? (
                                    <EmptyState icon={<Bookmark size={22} />} message="None saved" />
                                ) : (
                                    savedFilters.map((f) => (
                                        <div className="search-panel__fav-row" key={f.key}>
                                            <Item
                                                active={activeFilterKeys.has(f.key)}
                                                label={f.label}
                                                onClick={() => onToggleFilter(f.key)}
                                            />
                                            <button
                                                type="button"
                                                className="search-panel__chip-close"
                                                aria-label={`Delete ${f.label}`}
                                                onClick={() => onDeleteSaved(f.key)}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                            <button
                                type="button"
                                className="search-panel__card-footer"
                                onClick={openSaveDialog}
                                disabled={!canSave}
                                title={canSave ? "Save current search as favourite" : "Add filters or a group-by first"}
                            >
                                <Plus size={12} />
                                <span>Save Current</span>
                            </button>
                        </div>
                    </div>
                    )}
                </div>
            ) : null}

            {/* Floating Custom-Group dialog — opens *over* the popover so the
                main popover layout stays stable. */}
            {customDialogOpen ? (
                <CustomGroupDialog
                    fields={customGroupFields}
                    initialSelection={groupBy}
                    onApply={(fields) => {
                        // Auto-encode :month for date/datetime entries so the
                        // chip dropdown lights up with a bucket immediately.
                        setWithBucket(fields);
                        // Promote the dialog's selection into the session list
                        // (bare field names — the session list keys by field).
                        onAddSessionCustomGroupFields(fields.map(groupByEntryField));
                        closeCustomDialog();
                    }}
                    onCancel={closeCustomDialog}
                />
            ) : null}

            {saveDialogOpen ? (
                <SaveFavouriteDialog
                    onSave={(name) => {
                        onSaveCurrent(name);
                        closeSaveDialog();
                    }}
                    onCancel={closeSaveDialog}
                />
            ) : null}
        </div>
    );
}

function Item({
    active,
    label,
    onClick,
    viewProps,
}: {
    active: boolean;
    label: string;
    onClick: () => void;
    // e2e contract hooks (data-viewid + the keyed data-*-key) spread onto the
    // option button so the SearchDriver can target filters / group-bys by key.
    viewProps?: Record<string, string>;
}) {
    return (
        <button type="button" className={`search-panel__item ${active ? "is-active" : ""}`} onClick={onClick} {...viewProps}>
            <span className="search-panel__item-tick" aria-hidden="true" />
            <span className="search-panel__item-label">{label}</span>
        </button>
    );
}

function EmptyState({ icon, message }: { icon: ReactNode; message: string }) {
    return (
        <div className="search-panel__card-empty">
            <span className="search-panel__card-empty-icon">{icon}</span>
            <span className="search-panel__card-empty-msg">{message}</span>
        </div>
    );
}

interface CustomGroupDialogProps {
    fields: FieldDef[];
    initialSelection: string[];
    onApply: (fields: string[]) => void;
    onCancel: () => void;
}

// Floating multi-check Custom Group dialog. Stages its own ordered selection,
// only commits on Apply. Edit mode = `initialSelection` non-empty (pre-checks
// existing group fields in their original order). Anchored as a sibling of
// `.search-panel__popover`, positioned via CSS over the popover, with its own
// outside-click handler. Apply replaces the entire group-by stack.
function CustomGroupDialog({ fields, initialSelection, onApply, onCancel }: CustomGroupDialogProps) {
    // Ordered selection — preserves the order in which fields were checked,
    // so nesting follows click order. Seeded from the existing groupBy when
    // entering edit mode.
    const [order, setOrder] = useState<string[]>(initialSelection);
    const [query, setQuery] = useState("");
    const selected = useMemo(() => new Set(order), [order]);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onCancel();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCancel();
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [onCancel]);

    const q = query.trim().toLowerCase();
    const filtered = q
        ? fields.filter((f) => (f.label ?? f.name).toLowerCase().includes(q) || f.name.toLowerCase().includes(q))
        : fields;

    const toggle = (name: string) => {
        setOrder((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
    };

    return (
        <div className="custom-group-dialog" ref={ref} role="dialog" aria-label="Custom group by">
            <div className="custom-group-dialog__head">
                <Group size={14} />
                <span className="custom-group-dialog__title">
                    Custom Group By {initialSelection.length > 0 ? <em>(editing)</em> : null}
                </span>
                <button
                    type="button"
                    className="custom-group-dialog__close"
                    onClick={onCancel}
                    aria-label="Close"
                >
                    <X size={14} />
                </button>
            </div>
            <div className="custom-group-dialog__search">
                <Search size={13} />
                <input
                    autoFocus
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search fields…"
                />
            </div>
            {order.length > 0 ? (
                <div className="custom-group-dialog__order">
                    <span className="custom-group-dialog__order-label">Order:</span>
                    {order.map((name, i) => {
                        const def = fields.find((f) => f.name === name);
                        return (
                            <span key={name} className="custom-group-dialog__order-chip">
                                {i > 0 ? <ChevronDown size={10} style={{ transform: "rotate(-90deg)" }} /> : null}
                                {def?.label ?? name}
                            </span>
                        );
                    })}
                </div>
            ) : null}
            <div className="custom-group-dialog__list">
                {filtered.length === 0 ? (
                    <div className="search-panel__card-empty">
                        <span className="search-panel__card-empty-icon"><Search size={20} /></span>
                        <span className="search-panel__card-empty-msg">No matching fields</span>
                    </div>
                ) : null}
                {filtered.map((f) => {
                    const active = selected.has(f.name);
                    const idx = order.indexOf(f.name);
                    return (
                        <button
                            type="button"
                            key={f.name}
                            className={`custom-group-dialog__item ${active ? "is-active" : ""}`}
                            onClick={() => toggle(f.name)}
                        >
                            <span className={`custom-group-dialog__check ${active ? "is-checked" : ""}`}>
                                {active ? <Check size={12} /> : null}
                            </span>
                            <span className="custom-group-dialog__item-label">{f.label ?? f.name}</span>
                            {active ? (
                                <span className="custom-group-dialog__item-order">{idx + 1}</span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
            <div className="custom-group-dialog__footer">
                <button type="button" className="custom-group-dialog__btn" onClick={onCancel}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="custom-group-dialog__btn custom-group-dialog__btn--primary"
                    onClick={() => onApply(order)}
                >
                    <Sparkles size={12} />
                    Apply
                </button>
            </div>
        </div>
    );
}

interface SaveFavouriteDialogProps {
    onSave: (name: string) => void;
    onCancel: () => void;
}

// Floating "Save Current" dialog — same layout shell as CustomGroupDialog, so
// every secondary dialog feels consistent. Placeholder rows hint at planned
// options (default load, share with all users) without committing to wiring
// them yet.
function SaveFavouriteDialog({ onSave, onCancel }: SaveFavouriteDialogProps) {
    const [name, setName] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onCancel();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCancel();
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [onCancel]);

    const trimmed = name.trim();
    const submit = () => {
        if (!trimmed) return;
        onSave(trimmed);
    };

    return (
        <div className="custom-group-dialog" ref={ref} role="dialog" aria-label="Save current search">
            <div className="custom-group-dialog__head">
                <Bookmark size={14} />
                <span className="custom-group-dialog__title">Save Current Search</span>
                <button
                    type="button"
                    className="custom-group-dialog__close"
                    onClick={onCancel}
                    aria-label="Close"
                >
                    <X size={14} />
                </button>
            </div>
            <div className="save-fav-dialog__body">
                <label className="save-fav-dialog__field">
                    <span className="save-fav-dialog__label">Name</span>
                    <input
                        autoFocus
                        type="text"
                        value={name}
                        placeholder="My favourite search"
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submit();
                        }}
                        className="save-fav-dialog__input"
                    />
                </label>
                <label className="save-fav-dialog__check is-disabled" title="Coming soon">
                    <input type="checkbox" disabled />
                    <span>Load as default</span>
                    <span className="save-fav-dialog__soon">Soon</span>
                </label>
                <label className="save-fav-dialog__check is-disabled" title="Coming soon">
                    <input type="checkbox" disabled />
                    <span>Share with all users</span>
                    <span className="save-fav-dialog__soon">Soon</span>
                </label>
            </div>
            <div className="custom-group-dialog__footer">
                <button type="button" className="custom-group-dialog__btn" onClick={onCancel}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="custom-group-dialog__btn custom-group-dialog__btn--primary"
                    onClick={submit}
                    disabled={!trimmed}
                >
                    <Bookmark size={12} />
                    Save
                </button>
            </div>
        </div>
    );
}
